import Konva from 'konva';
import {
  addToNode,
  createImage,
  createLayer,
  findLayer,
  getNodeAttrs,
  getNodeChildrenArray,
  getNodeStage,
  getStagePointerPosition,
  setNodeGlobalCompositeOperation,
  toCanvas,
} from '@picsart/web-layering/rendering/konva/selectors';
import { Line as LineType, Stage } from '@picsart/web-layering/types';

import aiService from 'services/aiService';
import loadingImage from 'utils/images/loadingImage';
import { scrollBarsLayerId } from 'configs/constants';
import errorLogger from 'services/errorLogger';

import { SettingDataType } from './EraserSettings';
import {
  eraserSettingsData,
  EraserCutoutEnum,
} from './config';
import { batchDrawNode } from './selectors';

const getDefaultSettings = () => eraserSettingsData.reduce((acc, item) => ({
  ...acc,
  [item.type]: item.defaultValue,
}), {} as SettingDataType);

const defaultPosition = {
  x: 0,
  y: 0,
};

const invertMask = (
  mask: HTMLImageElement | HTMLCanvasElement | undefined,
  image: HTMLImageElement | undefined,
  width: number,
  height: number,
): HTMLCanvasElement | undefined => {
  if (!image || !mask) {
    return;
  }

  const layer = createLayer({
    width,
    height,
    listening: false,
  });
  const original = createImage({
    width,
    height,
    image,
  });
  const maskImage = createImage({
    width,
    height,
    image: mask,
  });

  addToNode(layer, original);
  addToNode(layer, setNodeGlobalCompositeOperation(maskImage, 'destination-out'));
  return toCanvas(layer, {
    pixelRatio: image.width / width,
    width,
    height,
  });
};

const cutoutImage = async (
  mask: HTMLImageElement | HTMLCanvasElement,
  image: HTMLImageElement | undefined,
  width: number,
  height: number,
  cutOutKeepMask: boolean,
  type: string,
): Promise<HTMLCanvasElement | HTMLImageElement | undefined> => {
  if (image) {
    try {
      const personMaskUrl = type === EraserCutoutEnum.Person
        ? await aiService.personCutout(image)
        : type === EraserCutoutEnum.Object
          ? await aiService.objectBackgroundRemove(image)
          : await aiService.backgroundRemove(image);

      const personMask = await loadingImage(personMaskUrl);

      if (!cutOutKeepMask) {
        return personMask;
      }

      const layer = createLayer({
        width,
        height,
        listening: false,
      });
      const person = createImage({
        width,
        height,
        image: personMask,
      });
      const maskImage = createImage({
        width,
        height,
        image: mask,
      });
      const original = createImage({
        width,
        height,
        image,
      });

      layer.add(maskImage);
      layer.add(person);
      layer.add(original.globalCompositeOperation('source-in'));

      return layer.toCanvas({
        pixelRatio: image.width / width,
        width,
        height,
      });
    } catch (err) {
      errorLogger.trigger(err as Error, 'error');
    }
  }
};

const getWatermarkAttrs = (wmImage: HTMLImageElement, stageWidth: number, stageHeight: number) => {
  const scaleImage = Math.max(
    stageWidth / wmImage.width,
    stageHeight / wmImage.height,
  );

  const x = (stageWidth / 2) - (wmImage.width / 2) * scaleImage;
  const y = (stageHeight / 2) - (wmImage.height / 2) * scaleImage;
  const width = wmImage.width * scaleImage;
  const height = wmImage.height * scaleImage;

  return {
    x,
    y,
    opacity: 0.6,
    width,
    height,
  };
};

const toggleScrollBar = (lineRefCurrent: LineType, isShow: boolean) => {
  const stage = lineRefCurrent && getNodeStage(lineRefCurrent);

  if (!stage) {
    return;
  }

  const scrollLayers = findLayer(stage, `#${scrollBarsLayerId}`);

  if (!scrollLayers) {
    return;
  }

  getNodeChildrenArray(scrollLayers).forEach(scrollBar => {
    scrollBar[isShow ? 'show' : 'hide']();
  });

  batchDrawNode(scrollLayers);
};

const getScaledPointer = (stage: Stage | null) => {
  if (!stage) {
    return defaultPosition;
  }

  const pointerPosition = getStagePointerPosition(stage) || defaultPosition;

  const {
    scaleX: stageScaleX,
    scaleY: stageScaleY,
  } = getNodeAttrs(stage);

  let xScrollLayer = 0;
  let yScrollLayer = 0;

  const firstLayer = findLayer(stage, el => el instanceof Konva.Layer);
  if (firstLayer) {
    const { x: scrollLayerX, y: scrollLayerY } = getNodeAttrs(firstLayer);

    xScrollLayer = scrollLayerX;
    yScrollLayer = scrollLayerY;
  }

  return {
    x: pointerPosition.x / stageScaleX - xScrollLayer,
    y: pointerPosition.y / stageScaleY - yScrollLayer,
  };
};

const calculatePercent = (val: number, min: number, max: number): number => (100 * (val - min)) / (max - min);

export {
  invertMask,
  cutoutImage,
  getWatermarkAttrs,
  getDefaultSettings,
  toggleScrollBar,
  getScaledPointer,
  calculatePercent,
};
